import React, { useMemo, useState } from 'react';
import {
  AuditTrail, DataTable, DecisionPanel, EmptyState, FilterBar, ImageViewer, Pagination,
  SearchInput, SplitView, StatusBadge, Toolbar, exportFilename, exportRows,
  useTableDensity, useToast,
  type ActiveFilter, type Column, type RejectReason, type SortState,
  resolveLink, type NavLinkComponent,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  MANUAL_REASON_HINT, MANUAL_REASON_LABEL, MEMBER_TYPE_DISCOUNT, MEMBER_TYPE_FAMILY,
  MEMBER_TYPE_LABEL, TYPE_REQUESTS, requestAudit,
  type ManualReason, type MemberType, type TypeRequest,
} from './memberTypeData';

/**
 * MemberTypeApprovalScreen — คิวอนุมัติประเภทสมาชิก (FR-ADM-08, FR-ADM-09)
 *
 * หลังใช้การยืนยันด้วยโดเมนอีเมล คิวนี้เหลือเฉพาะ 5 กรณีที่ระบบตัดสินเองไม่ได้:
 *   โดเมนไม่อยู่ในรายการ · พนักงานเกษียณ · กลุ่ม B2B · ร้านขายยา · โดเมนที่ถูกปิดไปแล้ว
 *
 * โครงหน้าเหมือนคิวสลิป (SplitView + ImageViewer + DecisionPanel) โดยเจตนา —
 * เจ้าหน้าที่คนเดียวกันสลับไปมาระหว่างสองคิวในหนึ่งวัน คีย์ลัดและตำแหน่งปุ่มจึงต้องตรงกัน
 *
 * ต่างจากคิวสลิปสองข้อ:
 *   1. เอกสารมีหลายไฟล์ต่อคำขอ (ภพ.20 · หนังสือรับรอง · สมุดบัญชี · ใบทะเบียนร้าน)
 *      จึงมีแถบสลับไฟล์เหนือ ImageViewer และตรวจว่าแนบครบตามประเภทหรือยัง
 *   2. การอนุมัติเปลี่ยนโครงสร้างราคาของสมาชิกทันที (FR-MTYPE-06) ไม่ใช่แค่เพิ่มคะแนน
 *      แผงตัดสินจึงบอกอัตราส่วนลดที่จะได้ก่อนกดอนุมัติ
 */

type ScreenState = 'ready' | 'loading' | 'error';
type Tab = 'pending' | 'approved' | 'rejected' | 'all';

/** เอกสารที่แต่ละกลุ่มต้องมีครบ ตาม SRS 3.2 */
const REQUIRED_DOCS: Record<'ptt' | 'b2b' | 'pharmacy', Array<{ kind: string; label: string }>> = {
  ptt: [{ kind: 'employee-card', label: 'รูปถ่ายบัตรพนักงาน' }],
  b2b: [
    { kind: 'por-20', label: 'ภพ.20' },
    { kind: 'company-cert', label: 'หนังสือรับรองบริษัท' },
    { kind: 'bankbook', label: 'หน้าสมุดบัญชี' },
  ],
  pharmacy: [
    { kind: 'shop-license', label: 'ใบทะเบียนร้านค้า' },
    { kind: 'por-20', label: 'ภพ.20' },
    { kind: 'company-cert', label: 'หนังสือรับรองบริษัท' },
  ],
};

const REJECT_REASONS: RejectReason[] = [
  { code: 'docs-incomplete', label: 'เอกสารที่แนบมาไม่ครบ กรุณาแนบเอกสารที่ขาดแล้วส่งคำขอใหม่', requiresDetail: true },
  { code: 'docs-unreadable', label: 'เอกสารที่แนบไม่ชัด อ่านข้อความไม่ได้ กรุณาถ่ายใหม่ให้เห็นข้อความครบทุกบรรทัด' },
  { code: 'name-mismatch', label: 'ชื่อในเอกสารไม่ตรงกับชื่อผู้สมัคร', requiresDetail: true },
  { code: 'not-in-group', label: 'บริษัทที่ระบุไม่อยู่ในกลุ่ม ปตท. ตามรายการที่บริษัทแจ้งไว้' },
  { code: 'wrong-type', label: 'ประเภทที่ขอไม่ตรงกับเอกสารที่แนบ กรุณาเลือกประเภทให้ตรงแล้วส่งใหม่', requiresDetail: true },
  { code: 'expired-doc', label: 'เอกสารที่แนบหมดอายุแล้ว กรุณาแนบฉบับที่ยังไม่หมดอายุ' },
  { code: 'duplicate', label: 'พบคำขอของบุคคลนี้ที่ได้รับอนุมัติแล้ว ไม่ต้องยื่นซ้ำ' },
  { code: 'other', label: 'เหตุผลอื่น', requiresDetail: true },
];

const ageText = (h: number) => (h < 24 ? `${h} ชม.` : `${Math.floor(h / 24)} วัน ${h % 24} ชม.`);

const SlaTag: React.FC<{ ageHours: number }> = ({ ageHours }) => {
  const rem = 72 - ageHours;
  const over = rem < 0;
  const warn = !over && ageHours >= 48;
  const tone = over || warn
    ? 'border-[var(--warning-border)] bg-[var(--warning-bg)] text-[var(--warning-text)]'
    : 'border-[var(--line-200)] bg-[var(--surface-100)] text-[var(--ink-700)]';
  return (
    <span className={`inline-flex items-center gap-[3px] rounded-[var(--radius-sm)] border px-[var(--space-15)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] ${tone}`}>
      {(over || warn) && <Icon name={over ? 'running_with_errors' : 'schedule'} size={13} />}
      {over ? `เกิน SLA ${Math.max(1, Math.floor(-rem / 24))} วัน` : warn ? `เหลือ ${rem} ชม.` : `เหลือ ${Math.floor(rem / 24)} วัน`}
    </span>
  );
};

const ReasonChip: React.FC<{ reason: ManualReason }> = ({ reason }) => {
  const blocking = reason === 'retired';
  return (
    <span
      title={MANUAL_REASON_HINT[reason]}
      className={`inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] ${
        blocking ? 'bg-[var(--warning-bg)] text-[var(--warning-text)]' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'
      }`}
    >
      <Icon name={blocking ? 'help' : 'alt_route'} size={14} />
      {MANUAL_REASON_LABEL[reason]}
    </span>
  );
};

const TypeChip: React.FC<{ type: MemberType }> = ({ type }) => {
  const d = MEMBER_TYPE_DISCOUNT[type];
  return (
    <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--blue-050)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--blue-900)]">
      <Icon name="badge" size={14} />
      {MEMBER_TYPE_LABEL[type]}
      {d !== undefined && <span className="bo-num text-[var(--ink-700)]">ส่วนลด {d}%</span>}
    </span>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{children}</span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

const inputCls =
  'min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

export interface MemberTypeApprovalScreenProps {
  /** host เป็นคนตัดสินว่ากดลิงก์แล้วไปไหน — ดู components/NavLink */
  linkComponent?: NavLinkComponent;
  initialState?: ScreenState;
}

export const MemberTypeApprovalScreen: React.FC<MemberTypeApprovalScreenProps> = ({ initialState = 'ready', linkComponent }) => {
  const Link = resolveLink(linkComponent);
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [requests, setRequests] = useState<TypeRequest[]>(TYPE_REQUESTS);
  const [tab, setTab] = useState<Tab>('pending');
  const [density] = useTableDensity();

  const [query, setQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState<'all' | ManualReason>('all');
  const [familyFilter, setFamilyFilter] = useState<'all' | 'ptt' | 'b2b' | 'pharmacy'>('all');
  const [sort, setSort] = useState<SortState | null>({ columnId: 'ageHours', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [activeId, setActiveId] = useState<string | null>('r0');
  const [docIndex, setDocIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  /* ---------------------------------------------------------- ตัวกรอง */

  const byTab = useMemo(() => requests.filter((r) => {
    if (tab === 'pending') return r.status === 'pending' || r.status === 'review' || r.status === 'overdue';
    if (tab === 'approved') return r.status === 'approved';
    if (tab === 'rejected') return r.status === 'rejected';
    return true;
  }), [requests, tab]);

  const filtered = useMemo(() => byTab.filter((r) => {
    if (reasonFilter !== 'all' && r.manualReason !== reasonFilter) return false;
    if (familyFilter !== 'all' && MEMBER_TYPE_FAMILY[r.requestedType] !== familyFilter) return false;
    if (query) {
      const q = query.trim().toLowerCase();
      if (!r.code.toLowerCase().includes(q) && !r.member.name.includes(q) && !r.member.memberCode.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [byTab, reasonFilter, familyFilter, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (r: TypeRequest): string | number =>
      sort.columnId === 'ageHours' ? r.ageHours
        : sort.columnId === 'requestedType' ? MEMBER_TYPE_LABEL[r.requestedType]
          : sort.columnId === 'member' ? r.member.name
            : r.code;
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);
  const active = useMemo(() => sorted.find((r) => r.id === activeId) ?? null, [sorted, activeId]);

  const counts = useMemo(() => ({
    pending: requests.filter((r) => r.status === 'pending' || r.status === 'review' || r.status === 'overdue').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
    all: requests.length,
  }), [requests]);

  /* --------------------------------------------------------- คอลัมน์ */

  const columns: Array<Column<TypeRequest>> = useMemo(() => [
    { id: 'code', header: 'รหัสคำขอ', width: 96, pinned: true, required: true, sortable: true, value: (r) => r.code, cell: (r) => <span className="bo-num" style={{ textAlign: 'left' }}>{r.code}</span> },
    {
      id: 'member', header: 'ผู้ขอ', width: 168, sortable: true, value: (r) => r.member.name,
      cell: (r) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-truncate">{r.member.name}</span>
          <span className="bo-num bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]" style={{ textAlign: 'left' }}>{r.member.memberCode}</span>
        </span>
      ),
    },
    { id: 'requestedType', header: 'ประเภทที่ขอ', width: 230, sortable: true, value: (r) => MEMBER_TYPE_LABEL[r.requestedType], cell: (r) => <TypeChip type={r.requestedType} /> },
    { id: 'manualReason', header: 'เหตุที่ต้องใช้คนตรวจ', width: 196, value: (r) => MANUAL_REASON_LABEL[r.manualReason], cell: (r) => <ReasonChip reason={r.manualReason} /> },
    {
      id: 'docs', header: 'เอกสาร', width: 116, align: 'center',
      value: (r) => `${r.documents.length}`,
      cell: (r) => {
        const need = REQUIRED_DOCS[MEMBER_TYPE_FAMILY[r.requestedType]];
        const missing = need.filter((n) => !r.documents.some((d) => d.kind === n.kind)).length;
        return missing > 0
          ? <span className="inline-flex items-center gap-1 text-[var(--warning-text)]"><Icon name="warning" size={14} filled />ขาด {missing}</span>
          : <span className="inline-flex items-center gap-1 text-[var(--ink-700)]"><Icon name="attach_file" size={14} />{r.documents.length} ไฟล์</span>;
      },
    },
    {
      id: 'ageHours', header: 'อายุคำขอ', width: 176, sortable: true, value: (r) => r.ageHours,
      cell: (r) => (
        <span className="inline-flex items-center gap-[var(--space-15)]">
          <span className="bo-num text-[length:var(--text-table-sm)]" style={{ textAlign: 'left' }}>{ageText(r.ageHours)}</span>
          <SlaTag ageHours={r.ageHours} />
        </span>
      ),
    },
    { id: 'status', header: 'สถานะ', width: 140, value: (r) => r.status, cell: (r) => <StatusBadge status={r.status} /> },
    {
      id: 'flags', header: 'ระบบตรวจพบ', width: 260,
      value: (r) => r.flags.join(' · '),
      cell: (r) => (r.flags.length
        ? <span className="inline-flex items-center gap-1 text-[var(--warning-text)]"><Icon name="flag" size={14} filled />{r.flags.length} เรื่อง</span>
        : <span className="text-[var(--ink-400)]">—</span>),
    },
  ], []);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (reasonFilter !== 'all') out.push({ id: 'reason', field: 'เหตุที่ต้องใช้คน', display: MANUAL_REASON_LABEL[reasonFilter] });
    if (familyFilter !== 'all') out.push({ id: 'family', field: 'กลุ่ม', display: familyFilter === 'ptt' ? 'กลุ่ม ปตท.' : familyFilter === 'b2b' ? 'B2B' : 'ร้านขายยา' });
    return out;
  }, [reasonFilter, familyFilter]);

  const clearAll = () => { setReasonFilter('all'); setFamilyFilter('all'); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: columns.map((c) => c.id), filename: exportFilename('คิวอนุมัติประเภทสมาชิก'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} คำขอแล้ว — บันทึกใน Audit Log` });
  };

  /* ------------------------------------------------------- การตัดสิน */

  const goNext = (decidedId: string) => {
    const idx = sorted.findIndex((r) => r.id === decidedId);
    const next = sorted.slice(idx + 1).find((r) => r.status === 'pending' || r.status === 'review' || r.status === 'overdue');
    setActiveId(next?.id ?? null);
    setDocIndex(0);
  };

  const decide = (r: TypeRequest, status: 'approved' | 'rejected', reasonLabel?: string, detail?: string) => {
    const before = requests;
    setBusy(true);
    setRequests((prev) => prev.map((x) => (x.id === r.id
      ? { ...x, status, decision: { at: new Date().toISOString(), byId: 'u1', reasonLabel, detail } }
      : x)));
    setBusy(false);
    goNext(r.id);
    toast.show({
      tone: status === 'approved' ? 'success' : 'info',
      message: status === 'approved'
        ? `อนุมัติ ${r.code} — ${r.member.name} เป็น ${MEMBER_TYPE_LABEL[r.requestedType]} แล้ว`
        : `ปฏิเสธ ${r.code} — แจ้งเหตุผลให้ ${r.member.name} แล้ว`,
      undo: { onUndo: () => { setRequests(before); setActiveId(r.id); }, seconds: 10 },
    });
  };

  /* ----------------------------------------------------------- ตาราง */

  const emptyState = activeFilters.length || query
    ? <EmptyState kind="no-results" title="ไม่พบคำขอที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
    : <EmptyState kind="queue-clear" title="ไม่มีคำขอค้างในคิว" description="คำขอเปลี่ยนประเภทสมาชิกที่ต้องใช้คนตรวจถูกตัดสินหมดแล้ว — คำขอที่ยืนยันด้วยโดเมนได้จะไม่เข้าคิวนี้" />;

  const listPane = (
    <>
      <DataTable<TypeRequest>
        columns={columns.filter((c) => ['code', 'member', 'requestedType', 'ageHours', 'status'].includes(c.id))}
        rows={rows}
        rowKey={(r) => r.id}
        caption={`คิวอนุมัติประเภทสมาชิก — ${sorted.length} รายการ`}
        state={screenState}
        rowCountHint={12}
        errorMessage="โหลดคิวไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={setSort}
        activeKey={activeId}
        focusedKey={activeId}
        onFocusedKeyChange={(k) => { setActiveId(k); setDocIndex(0); }}
        onOpenRow={(r) => { setActiveId(r.id); setDocIndex(0); }}
        density={density}
        persistKey="type-approval-list"
        isFlagged={(r) => r.flags.length > 0}
        className="min-h-0 flex-1 rounded-none border-0"
      />
      <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[25, 50, 100]} />
    </>
  );

  /* --------------------------------------------------- แผงรายละเอียด */

  const detailPane = active && (() => {
    const family = MEMBER_TYPE_FAMILY[active.requestedType];
    const need = REQUIRED_DOCS[family];
    const missing = need.filter((n) => !active.documents.some((d) => d.kind === n.kind));
    const doc = active.documents[Math.min(docIndex, Math.max(0, active.documents.length - 1))];
    const nextDoc = active.documents[docIndex + 1];
    const blocking = active.manualReason === 'retired';
    const decided = active.status === 'approved' || active.status === 'rejected';

    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-4)] py-[var(--space-25)]">
          <h2 className="bo-num m-0 text-[length:var(--text-section)] font-[var(--weight-semibold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{active.code}</h2>
          <StatusBadge status={active.status} size="md" />
          <ReasonChip reason={active.manualReason} />
          <SlaTag ageHours={active.ageHours} />
        </header>

        <div className="grid min-h-0 flex-1 gap-[var(--space-3)] p-[var(--space-3)]" style={{ gridTemplateColumns: 'minmax(340px,1.05fr) minmax(340px,1fr)' }}>
          {/* ---- เอกสารแนบ ---- */}
          <div className="flex min-h-0 flex-col gap-[var(--space-2)]">
            {active.documents.length > 1 && (
              <div role="tablist" aria-label="เอกสารแนบ" className="flex flex-wrap gap-[var(--space-1)]">
                {active.documents.map((d, i) => (
                  <button
                    key={d.id}
                    role="tab"
                    type="button"
                    aria-selected={i === docIndex}
                    onClick={() => setDocIndex(i)}
                    className={`inline-flex min-h-[var(--hit-table)] items-center gap-1 rounded-[var(--radius-sm)] border px-[var(--space-25)] text-[length:var(--text-table-sm)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                      i === docIndex
                        ? 'border-[var(--blue-700)] bg-[var(--blue-100)] font-[var(--weight-semibold)] text-[var(--blue-900)]'
                        : 'border-[var(--line-300)] bg-[var(--surface-0)] text-[var(--ink-700)] hover:bg-[var(--blue-050)]'
                    }`}
                  >
                    <Icon name="description" size={14} />
                    {d.label}
                  </button>
                ))}
              </div>
            )}

            {doc ? (
              <ImageViewer
                src={doc.url}
                alt={`${doc.label} ของคำขอ ${active.code}`}
                preloadSrc={nextDoc?.url}
                caption={`${doc.label} · อัปโหลด ${doc.uploadedAt}`}
                shortcutScope="zoom-only"
                className="min-h-0 flex-1"
              />
            ) : (
              <div className="flex min-h-[240px] flex-1 items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--line-300)] bg-[var(--surface-100)]">
                <EmptyState kind="no-data" title="ไม่มีเอกสารแนบ" description="ผู้ขอส่งคำขอโดยไม่แนบเอกสาร — ถ้าประเภทนี้ต้องมีเอกสาร ให้ปฏิเสธพร้อมระบุรายการที่ต้องแนบ" />
              </div>
            )}

            {doc?.extracted && (
              <dl className="grid grid-cols-2 gap-x-[var(--space-3)] gap-y-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-25)]">
                {Object.entries(doc.extracted).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ระบบอ่านได้: {k}</dt>
                    <dd className="bo-num m-0 text-[length:var(--text-table)] font-[var(--weight-medium)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{v}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </div>

          {/* ---- ข้อมูลและประวัติ ---- */}
          <div className="flex min-h-0 flex-col gap-[var(--space-3)] overflow-auto">
            {blocking && (
              <div role="alert" className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)]">
                <Icon name="gavel" size={19} filled className="text-[var(--warning-text)]" />
                <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                  <strong className="text-[var(--warning-text)]">ยังไม่มีนโยบายพนักงานเกษียณ</strong> —
                  ทั้งสิทธิ์ที่ได้และวิธีพิสูจน์ตัวตน (บัตรพนักงานหมดอายุแล้ว) ยังไม่ได้ข้อสรุปจากฝ่าย HR
                  ห้ามตัดสินคำขอกลุ่มนี้จนได้คำตอบ — พักไว้ในคิวและแจ้งหัวหน้าทีม
                </p>
              </div>
            )}

            {active.flags.length > 0 && (
              <section className="flex flex-col gap-[var(--space-15)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)]">
                <h3 className="m-0 flex items-center gap-[var(--space-15)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--warning-text)]">
                  <Icon name="flag" size={18} filled />
                  ระบบตรวจพบ {active.flags.length} เรื่องที่ควรดูก่อนตัดสิน
                </h3>
                <ul className="m-0 flex flex-col gap-[var(--space-1)] pl-[var(--space-5)]">
                  {active.flags.map((f, i) => (
                    <li key={i} className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">{f}</li>
                  ))}
                </ul>
              </section>
            )}

            {missing.length > 0 && (
              <section className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--error-border)] bg-[var(--error-bg)] p-[var(--space-3)]">
                <h3 className="m-0 flex items-center gap-[var(--space-15)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--error-text)]">
                  <Icon name="rule" size={18} filled />
                  เอกสารไม่ครบตามที่ประเภทนี้ต้องมี
                </h3>
                <ul className="m-0 flex flex-col gap-[var(--space-05)] pl-[var(--space-5)]">
                  {missing.map((m) => (
                    <li key={m.kind} className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">{m.label}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h3 className="m-0 mb-[var(--space-2)] flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">
                ข้อมูลที่ผู้ขอกรอก
                <a href={`/customers/${active.member.id}`} className="text-[length:var(--text-table-sm)] font-[var(--weight-regular)]">เปิด Customer 360</a>
              </h3>
              <div className="grid grid-cols-2 gap-[var(--space-3)]">
                <Field label="ผู้ขอ" hint={active.member.memberCode}>{active.member.name}</Field>
                <Field label="ประเภทที่ขอ" hint={MEMBER_TYPE_DISCOUNT[active.requestedType] !== undefined ? `ส่วนลด ${MEMBER_TYPE_DISCOUNT[active.requestedType]}%` : 'อัตราส่วนลดยังไม่กำหนด'}>
                  {MEMBER_TYPE_LABEL[active.requestedType]}
                </Field>
                <Field label="อีเมลที่ใช้ยืนยัน" hint={active.member.matchedDomainId ? 'โดเมนอยู่ในรายการ' : 'โดเมนไม่อยู่ในรายการ'}>
                  <span className="bo-num text-[length:var(--text-table)]" style={{ textAlign: 'left' }}>{active.member.email}</span>
                </Field>
                <Field label="เบอร์โทร">
                  <span className="bo-num" style={{ textAlign: 'left' }}>{active.member.phone}</span>
                </Field>
                {active.fields.map((f) => (
                  <Field key={f.label} label={f.label}>{f.value}</Field>
                ))}
              </div>
            </section>

            <section className="rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-3)]">
              <h3 className="m-0 mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ประวัติของคำขอนี้</h3>
              <AuditTrail entries={requestAudit(active) as never} compact />
            </section>
          </div>
        </div>

        {decided ? (
          <div className="flex items-center gap-[var(--space-2)] border-t border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)]">
            <StatusBadge status={active.status} size="md" />
            <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
              คำขอนี้ถูกตัดสินแล้ว{active.decision?.reasonLabel ? ` — ${active.decision.reasonLabel}` : ''}
            </p>
          </div>
        ) : (
          <DecisionPanel
            summary={
              <div className="flex flex-wrap items-center gap-[var(--space-4)]">
                <Field label="ประเภทเดิม">สมาชิกทั่วไป</Field>
                <Icon name="arrow_forward" size={20} className="text-[var(--ink-600)]" />
                <Field
                  label="ประเภทใหม่หลังอนุมัติ"
                  hint={MEMBER_TYPE_DISCOUNT[active.requestedType] !== undefined
                    ? `ราคาสินค้าจะเปลี่ยนเป็นราคาส่วนลด ${MEMBER_TYPE_DISCOUNT[active.requestedType]}% ทันที`
                    : 'อัตราส่วนลดของประเภทนี้ยังไม่กำหนด'}
                >
                  {MEMBER_TYPE_LABEL[active.requestedType]}
                </Field>
              </div>
            }
            warning={blocking
              ? 'กลุ่มพนักงานเกษียณยังไม่มีนโยบาย — ห้ามอนุมัติหรือปฏิเสธจนได้คำตอบจากฝ่าย HR'
              : missing.length > 0
                ? `เอกสารขาด ${missing.length} รายการ — ถ้าปฏิเสธ ให้ระบุรายการที่ต้องแนบในเหตุผลเพื่อให้ผู้ขอส่งใหม่ได้ถูก`
                : undefined}
            approveConsequence={
              <>
                {active.member.name} จะได้สิทธิ์ <strong>{MEMBER_TYPE_LABEL[active.requestedType]}</strong>{' '}
                และการยืนยันมีอายุ 1 ปี — ระบบจะเตือนให้ยืนยันใหม่ล่วงหน้า 30 วัน
              </>
            }
            reasons={REJECT_REASONS}
            busy={busy}
            shortcutsEnabled={!blocking}
            onApprove={() => decide(active, 'approved')}
            onReject={({ label, detail }) => decide(active, 'rejected', label, detail)}
          />
        )}
      </div>
    );
  })();

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="คิวอนุมัติประเภทสมาชิก"
        count={counts.pending}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหารหัสคำขอ ชื่อ หรือรหัสสมาชิก" width={300} />}
        end={
          <>
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
            <Link to="/settings/email-domains" className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] no-underline">
              <Icon name="dns" size={16} />จัดการโดเมนอีเมล
            </Link>
          </>
        }
      />

      {/* คิวนี้เหลือเฉพาะกรณีที่ระบบตัดสินเองไม่ได้ — อธิบายไว้เพื่อไม่ให้เข้าใจผิดว่าคำขอหาย */}
      <div className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--info-bg)] px-[var(--space-4)] py-[var(--space-2)]">
        <Icon name="info" size={19} filled className="text-[var(--info-text)]" />
        <p className="m-0 max-w-[106ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          คำขอที่ยืนยันด้วยโดเมนอีเมลในรายการได้สำเร็จ <strong>จะไม่เข้าคิวนี้</strong> —
          ที่เหลือคือกรณีที่ระบบตัดสินเองไม่ได้ ถ้าเห็นบริษัทในเครือที่โดเมนยังไม่อยู่ในรายการซ้ำ ๆ
          ให้เพิ่มโดเมนนั้นเพื่อไม่ให้คนถัดไปต้องเข้าคิว
        </p>
      </div>

      <div role="tablist" aria-label="สถานะคำขอ" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        {([['pending', 'รอตรวจสอบ', counts.pending], ['approved', 'อนุมัติแล้ว', counts.approved], ['rejected', 'ปฏิเสธ', counts.rejected], ['all', 'ทั้งหมด', counts.all]] as Array<[Tab, string, number]>).map(([id, label, n]) => {
          const on = tab === id;
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => { setTab(id); setPage(1); }}
              className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
              }`}
            >
              {label}
              <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                {n.toLocaleString('th-TH')}
              </span>
            </button>
          );
        })}
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={(id) => { if (id === 'reason') setReasonFilter('all'); if (id === 'family') setFamilyFilter('all'); setPage(1); }}
        onClearAll={clearAll}
        currentFilters={{ reasonFilter, familyFilter }}
        persistKey="type-approval"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          เหตุที่ต้องใช้คนตรวจ
          <select value={reasonFilter} onChange={(e) => { setReasonFilter(e.target.value as typeof reasonFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(Object.keys(MANUAL_REASON_LABEL) as ManualReason[]).map((r) => (
              <option key={r} value={r}>{MANUAL_REASON_LABEL[r]}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          กลุ่ม
          <select value={familyFilter} onChange={(e) => { setFamilyFilter(e.target.value as typeof familyFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            <option value="ptt">กลุ่ม ปตท.</option>
            <option value="b2b">B2B / องค์กร</option>
            <option value="pharmacy">ร้านขายยา</option>
          </select>
        </label>
      </FilterBar>

      {screenState === 'ready' ? (
        <SplitView
          persistKey="type-approval"
          hasDetail={Boolean(active)}
          list={<div className="flex min-h-0 flex-1 flex-col">{listPane}</div>}
          detail={detailPane}
          detailPlaceholder={
            <EmptyState
              kind="queue-clear"
              title={sorted.length ? 'เลือกคำขอจากรายการด้านซ้าย' : 'ไม่มีคำขอค้างในคิว'}
              description={sorted.length ? 'กด J / K เพื่อเลื่อน และ Enter เพื่อเปิด — A อนุมัติ · R ปฏิเสธ' : 'คำขอที่ต้องใช้คนตรวจถูกตัดสินหมดแล้ว'}
            />
          }
          className="min-h-0 flex-1"
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">{listPane}</div>
      )}
    </div>
  );
};

export default MemberTypeApprovalScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-08  คิวอนุมัติคำขอเปลี่ยนประเภทสมาชิก — แสดงวันเวลา ชื่อสมาชิก
              ประเภทที่ขอ สถานะ และเปิดรายละเอียดได้
   FR-ADM-09  หน้าตรวจคำขอ: ดูเอกสารแนบ อนุมัติ/ปฏิเสธพร้อมเหตุผล
              (ปฏิเสธบังคับกรอกเหตุผล — DecisionPanel ปิดปุ่มยืนยันไว้จนเลือกเหตุผล)
   FR-MTYPE-06 อนุมัติแล้วโครงสร้างราคาและสิทธิ์เปลี่ยนทันที — แผงตัดสินบอกอัตราส่วนลด
              ที่จะมีผลก่อนกด
   SRS 3.2    รายการเอกสารที่แต่ละประเภทต้องมี — ตรวจความครบและบอกรายการที่ขาด
   SRS 3.5    เงื่อนไขกลุ่ม PTT Group และการยืนยันสิทธิ์รายปี
   FR-ADM-37  ทุกการตัดสินเข้า Audit Log พร้อมเหตุผลและค่าเดิม→ค่าใหม่
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้

   ประเด็นค้าง (PROGRESS-REPORT 8.4): กลุ่มพนักงานเกษียณยังไม่มีนโยบาย
   หน้าจอปิดคีย์ลัด A/R และแสดงคำเตือนสำหรับคำขอกลุ่มนี้ เพื่อไม่ให้เจ้าหน้าที่ตัดสินเอง
   ============================================================================ */
